import { useState, useEffect } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import { Plus, Edit3, Trash2, Copy, Search, FileText, Loader2, Eye } from "lucide-react";
import { Select, Checkbox } from "antd";
import EmailBodyEditor from "../../components/email/EmailBodyEditor";
import EmailPreviewModal from "../../components/email/EmailPreviewModal";
import LeadWelcomeCard from "../../components/email/LeadWelcomeCard";
import LeadWelcomeWhatsAppCard from "../../components/email/LeadWelcomeWhatsAppCard";
import DemoFeedbackCard from "../../components/email/DemoFeedbackCard";
import DemoReminderCard from "../../components/email/DemoReminderCard";
import NurtureCard from "../../components/email/NurtureCard";
import useApiMethods from "../../lib/useApiMethods";
import { EMAIL_TEMPLATES_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

const CATEGORY_OPTIONS = [
    { value: 'sales_outreach', label: 'Sales Outreach' },
    { value: 'follow_up',      label: 'Follow Up' },
    { value: 'proposal',       label: 'Proposal' },
    { value: 'meeting',        label: 'Meeting' },
    { value: 'support',        label: 'Support' },
    { value: 'marketing',      label: 'Marketing' },
];

const CATEGORY_COLORS = {
    sales_outreach: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    follow_up:      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    proposal:       'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    meeting:        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    support:        'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    marketing:      'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
};

// {{first_name}} / {{name}} are filled by the new lead welcome email and the preview.
// {{verification_link}} is the welcome email's personal "confirm your details" link.
const VAR_CHIPS = ['{{first_name}}', '{{customer_name}}', '{{company_name}}', '{{sales_rep_name}}', '{{verification_link}}'];

const EMPTY_FORM = {
    name: '', category: 'sales_outreach', subject: '', body_html: '', is_global: false,
};

export default function EmailTemplates() {
    const { fetch, create, update, remove } = useApiMethods();
    const [templates, setTemplates] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null); // null = new
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const loadTemplates = async () => {
        setLoading(true);
        const res = await fetch(EMAIL_TEMPLATES_URL);
        if (res?.status === 'success') setTemplates(res.data || []);
        setLoading(false);
    };

    useEffect(() => { loadTemplates(); }, []);

    const openCreate = () => {
        setEditingTemplate(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (t) => {
        setEditingTemplate(t);
        setForm({
            name: t.name,
            category: t.category,
            subject: t.subject,
            body_html: t.body_html,
            is_global: t.is_global,
        });
        setDialogOpen(true);
    };

    const openClone = (t) => {
        setEditingTemplate(null);
        setForm({
            name: `Copy of ${t.name}`,
            category: t.category,
            subject: t.subject,
            body_html: t.body_html,
            is_global: false,
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.subject.trim() || !form.body_html.trim()) {
            ToastMsg('error', 'Name, subject, and body are required');
            return;
        }
        setSaving(true);
        let res;
        if (editingTemplate) {
            res = await update(EMAIL_TEMPLATES_URL, { id: editingTemplate.id, ...form });
        } else {
            res = await create(EMAIL_TEMPLATES_URL, form);
        }
        setSaving(false);
        if (res?.status === 'success') {
            ToastMsg('success', editingTemplate ? 'Template updated' : 'Template created');
            setDialogOpen(false);
            loadTemplates();
        } else {
            ToastMsg('error', res?.message || 'Save failed');
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        const res = await remove(`${EMAIL_TEMPLATES_URL}?id=${deleteTarget.id}`);
        setDeleting(false);
        if (res?.status === 'success') {
            ToastMsg('success', 'Template deleted');
            setDeleteTarget(null);
            loadTemplates();
        } else {
            ToastMsg('error', res?.message || 'Delete failed');
        }
    };

    const copyVar = (v) => {
        navigator.clipboard.writeText(v);
        ToastMsg('info', `Copied ${v}`);
    };

    const filtered = templates.filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        (t.category || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="p-6 space-y-6">
            <LeadWelcomeCard templates={templates} onCreateTemplate={openCreate} />
            <LeadWelcomeWhatsAppCard />
            <DemoReminderCard templates={templates} onCreateTemplate={openCreate} />
            <DemoFeedbackCard templates={templates} onCreateTemplate={openCreate} />
            <NurtureCard templates={templates} onCreateTemplate={openCreate} />

            <ContainerCard>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Email Templates</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Reusable email templates with merge variables
                        </p>
                    </div>
                    <Button onClick={openCreate} className="flex items-center gap-2 text-sm">
                        <Plus className="h-4 w-4" /> Add Template
                    </Button>
                </div>

                <div className="mb-4 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search templates..."
                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                            <FileText className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {search ? 'No templates found' : 'No templates yet'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                                    <th className="pb-3 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                    <th className="pb-3 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                                    <th className="pb-3 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Global</th>
                                    <th className="pb-3 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created By</th>
                                    <th className="pb-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {filtered.map(t => (
                                    <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                        <td className="py-3 pr-4">
                                            <p className="font-medium text-gray-800 dark:text-gray-100">{t.name}</p>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{t.subject}</p>
                                        </td>
                                        <td className="py-3 pr-4">
                                            <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full", CATEGORY_COLORS[t.category] || 'bg-gray-100 text-gray-600')}>
                                                {CATEGORY_OPTIONS.find(o => o.value === t.category)?.label || t.category}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-4">
                                            {t.is_global
                                                ? <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">Global</span>
                                                : <span className="text-xs text-gray-400">—</span>
                                            }
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-gray-500 dark:text-gray-400">
                                            {t.created_by_name || '—'}
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => openEdit(t)} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
                                                    <Edit3 className="h-3.5 w-3.5" />
                                                </button>
                                                <button onClick={() => openClone(t)} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
                                                    <Copy className="h-3.5 w-3.5" />
                                                </button>
                                                <button onClick={() => setDeleteTarget(t)} className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400 dark:text-red-500">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </ContainerCard>

            {/* Create / Edit Dialog */}
            <DialogModal
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title={editingTemplate ? 'Edit Template' : 'New Template'}
                width="800px"
                showActions={false}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Template Name *</label>
                            <Input
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="e.g. Introduction Email"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Category *</label>
                            <Select
                                className="w-full"
                                value={form.category}
                                onChange={v => setForm(p => ({ ...p, category: v }))}
                                options={CATEGORY_OPTIONS}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Subject *</label>
                        <Input
                            value={form.subject}
                            onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                            placeholder="e.g. Introduction from {{sales_rep_name}}"
                        />
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-[10px] text-gray-400">Variables:</span>
                            {VAR_CHIPS.map(v => (
                                <button
                                    key={v}
                                    onClick={() => copyVar(v)}
                                    title="Click to copy"
                                    className="px-1.5 py-0.5 text-[10px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300"
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Body *</label>
                            <div className="flex items-center gap-2 flex-wrap">
                                {VAR_CHIPS.map(v => (
                                    <button
                                        key={v}
                                        onClick={() => copyVar(v)}
                                        title="Click to copy"
                                        className="px-1.5 py-0.5 text-[10px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300"
                                    >
                                        {v}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <EmailBodyEditor
                            value={form.body_html}
                            onChange={v => setForm(p => ({ ...p, body_html: v }))}
                            height={300}
                            placeholder="Write the email. Use {{name}} for the recipient's name."
                        />
                        <div className="flex justify-end pt-2">
                            <Button variant="outline" size="sm" type="button"
                                    onClick={() => setPreviewOpen(true)}
                                    disabled={!form.body_html?.trim()}
                                    className="flex items-center gap-1.5 text-xs">
                                <Eye className="h-3.5 w-3.5" /> Preview email
                            </Button>
                        </div>
                    </div>

                    <div>
                        <Checkbox
                            checked={form.is_global}
                            onChange={e => setForm(p => ({ ...p, is_global: e.target.checked }))}
                        >
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Make this template available to all users (Global)
                            </span>
                        </Checkbox>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="text-sm">Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="text-sm flex items-center gap-2">
                            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                            {editingTemplate ? 'Save Changes' : 'Create Template'}
                        </Button>
                    </div>
                </div>
            </DialogModal>

            {/* Delete Confirm */}
            {deleteTarget && (
                <ConfirmPopupModal
                    open={!!deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={handleDelete}
                    loading={deleting}
                    title="Delete Template"
                    message={`Are you sure you want to delete "${deleteTarget.name}"? This cannot be undone.`}
                />
            )}

            <EmailPreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                body_html={form.body_html}
                subject={form.subject}
            />
        </div>
    );
}
